import {
  AuthContext,
  isAuthenticated,
  useCurrentUser,
  useUserRoutes,
} from '@cosuno/authentication-frontend';
import {
  Avatar,
  COLOR,
  DropdownActionSeparator,
  DropdownMenu,
  Icon,
  IconClickable,
  Tooltip,
  useQueryMatches,
} from '@cosuno/cosuno-ui';
import { isCompanyFeatureEnabled } from '@cosuno/feature-flags-frontend';
import { NavigationConfirmationEnvironmentContext } from '@cosuno/react-router';
import React, { useContext } from 'react';
import { NavLink } from 'react-router-dom';

import { COMPANY_TYPE, USER_TYPE } from '~/__gql__/graphql';
import { toast } from '~/packages/Toast';
import { useUiState } from '~/shared/contexts/uiStateContext';
import WorkspaceContext from '~/shared/contexts/workspaceContext';
import useAnalytics from '~/shared/hooks/useAnalytics';
import useTranslation from '~/shared/hooks/useTranslation';
import { env } from '~/shared/utils/env';
import { getUserName } from '~/shared/utils/names';
import { isAgentWithAccessToCompanyProfile } from '~/shared/utils/user';

import BeamerTrigger from './BeamerTrigger';
import CelloTrigger from './CelloTrigger';
import Messages from './Messages';
import Notifications from './Notifications';
import {
  StyledPremiumBadge,
  TopbarControls,
  TopbarWrapper,
  UserDropdownTrigger,
  Username,
  Workspace,
  WorkspaceListItem,
  WorkspaceListItemIcon,
  WorkspaceListItemName,
  Wrapper,
} from './Styles';
import Tasks from './Tasks';

const AuthenticatedContent: React.FC = () => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const authContext = useContext(AuthContext);
  const user = useCurrentUser();
  const workspaceContext = useContext(WorkspaceContext);
  const routes = useUserRoutes();
  const navigationContext = useContext(NavigationConfirmationEnvironmentContext);
  const { isMobile } = useQueryMatches();
  const { setIsMainNavigationCollapsed } = useUiState();

  if (!navigationContext || !isAuthenticated(authContext)) return null;

  const { tryNavigatingAway } = navigationContext;

  const { authState, logout } = authContext;
  const { firstName, lastName, workspaces } = authState.user;

  const isCelloEnabled = isCompanyFeatureEnabled(user?.company, 'conditional-cello-widget');

  const shouldDisplayCello = env.REACT_APP_CELLO_DISABLED !== 'true' && isCelloEnabled;

  const shouldDisplayNewFeatures = env.REACT_APP_BEAMER_DISABLED !== 'true';

  const currentWorkspace = workspaces.find(({ isCurrent }) => isCurrent);
  const sortedWorkspaces = workspaces
    .slice()
    .sort((a, b) => a.company.name.localeCompare(b.company.name));

  const switchToWorkspace = async (workspaceId: string) => {
    try {
      await authContext.switchToWorkspace(workspaceId);
    } catch (error) {
      toast.error(error);
    }
  };

  const isPrincipalAdmin =
    user?.company.type === COMPANY_TYPE.principal && user?.type === USER_TYPE.administrator;

  return (
    <TopbarWrapper>
      <TopbarControls>
        {!isMobile && <Tasks />}
        <Messages />
        <Notifications />
        {shouldDisplayCello && !isMobile && <CelloTrigger />}
        {shouldDisplayNewFeatures && !isMobile && <BeamerTrigger />}
        {user?.company.config.premiumEnabled && user.company.type === COMPANY_TYPE.agent && (
          <Tooltip<HTMLDivElement>
            isTriggerElementClickable={false}
            bodyText={t('topBar:premiumTooltip')}
          >
            {({ ref, getReferenceProps }) => (
              <StyledPremiumBadge ref={ref} {...getReferenceProps()} />
            )}
          </Tooltip>
        )}
        <DropdownMenu.Container
          isLarge
          renderTrigger={({ isTriggered, toggleTrigger }) => (
            <UserDropdownTrigger
              onClick={() => {
                if (!isTriggered) trackEvent('topNavigationOpen');
                toggleTrigger();
              }}
            >
              <Avatar name={currentWorkspace?.company.name} />
              {!isMobile && (
                <>
                  <Wrapper>
                    <Workspace data-cy-workspace-name>{currentWorkspace?.company.name}</Workspace>
                    <Username>{getUserName({ firstName, lastName })}</Username>
                  </Wrapper>
                  <Icon
                    type={isTriggered ? 'chevron-up' : 'chevron-down'}
                    color={COLOR.textSecondary}
                  />
                </>
              )}
            </UserDropdownTrigger>
          )}
        >
          {sortedWorkspaces.length > 1 && (
            <>
              {sortedWorkspaces.map((workspace) => (
                <DropdownMenu.Action
                  key={workspace.id}
                  disabled={workspace.isCurrent}
                  onClick={() => {
                    workspaceContext.switch(() => {
                      void switchToWorkspace(workspace.id);
                    });
                  }}
                >
                  <WorkspaceListItem>
                    <WorkspaceListItemName bold={workspace.isCurrent}>
                      {workspace.company.name}
                    </WorkspaceListItemName>
                    {workspace.isCurrent && <WorkspaceListItemIcon />}
                  </WorkspaceListItem>
                </DropdownMenu.Action>
              ))}
              <DropdownActionSeparator />
            </>
          )}
          <DropdownMenu.Action
            forwardedAs={NavLink}
            to={routes.profile()}
            onClick={() => trackEvent('topNavigationMyAccount')}
          >
            {t('profile:myAccount')}
          </DropdownMenu.Action>
          {(isAgentWithAccessToCompanyProfile(user) || isPrincipalAdmin) && ( // TODO: find a way to use new permission system
            <DropdownMenu.Action
              forwardedAs={NavLink}
              to={routes.company()}
              onClick={() => trackEvent('topNavigationMyCompany')}
            >
              {t('company:company')}
            </DropdownMenu.Action>
          )}
          <DropdownMenu.Action
            data-cy-logout
            onClick={() => {
              trackEvent('authSignOut');
              tryNavigatingAway(() => logout());
            }}
          >
            {t('auth:signOut')}
          </DropdownMenu.Action>
        </DropdownMenu.Container>
      </TopbarControls>
      {isMobile && (
        <IconClickable
          onClick={() => setIsMainNavigationCollapsed((currentValue) => !currentValue)}
          type="menu"
          size={24}
        />
      )}
    </TopbarWrapper>
  );
};

export default AuthenticatedContent;
